import { PredicateFunction, Query } from '@msw/data'
import { apiBaseUrl } from 'app/src/msw/companyagreement/api/apiBaseUrl'
import { productCollection } from 'app/src/msw/companyagreement/initialSeed/products'
import type { companyagreementApi } from 'app/src/services/welfareportal'
import { http, HttpResponse, type DefaultBodyType } from 'msw'

/**
 * Handles GET requests to the `/products` endpoint by retrieving products from the `productCollection`
 * @returns A paginated list of products that match the query parameters, or an empty list if no such products exist
 */
export const getProducts = http.get<object, DefaultBodyType, companyagreementApi.ProductPaginated>(
  apiBaseUrl + '/products',
  async ({ request }) => {
    // set of request parameters
    const url = new URL(request.url)
    const key = url.searchParams.get('key')
    const limit: number = Number.parseInt(url.searchParams.get('pagingLimit') ?? '10')
    const offset: number = Number.parseInt(url.searchParams.get('pagingOffset') ?? '0')
    const onlyFavourites: boolean = Boolean(url.searchParams.get('onlyFavourites') === 'true')
    const categoryId = url.searchParams.get('categoryId') ?? ''
    const kind = url.searchParams.get('serviceCategory') ?? ''
    const parentCategoryId = url.searchParams.get('parentCategoryId') ?? ''
    const catalog = url.searchParams.get('catalog') === 'discount'

    console.log('Received request for getProducts with params:', {
      key,
      limit,
      offset,
      onlyFavourites,
      categoryId,
      kind,
      parentCategoryId,
      catalog,
    })

    // empty response
    const emptyResponse: companyagreementApi.ProductPaginated = { paging: { limit, offset, total: 0 }, items: [] }

    /**
     * Converts an array of products into a paginated response.
     * @param products The array of products to paginate.
     * @returns A paginated response containing the products.
     */
    const convertToPaginatedResponse = (products: companyagreementApi.Product[]): companyagreementApi.ProductPaginated => ({
      paging: { limit, offset, total: products.length },
      items: products
        .map((p) => ({
          productId: p.productId,
          productName: p.productName,
          description: p.description,
          mainImage: p.mainImage,
          geoLocation: p.geoLocation,
          geoAddress: p.geoAddress,
          weight: p.weight,
          isFavourite: p.isFavourite,
          additionalInfo: p.additionalInfo,
        }))
        .slice(offset, offset + limit),
    })

    // If no name is provided and the category is either 'DIRECT' or 'DISCOUNT', return an empty response
    if (!key && (categoryId === 'DIRECT' || categoryId === 'DISCOUNT')) {
      return HttpResponse.json(emptyResponse)
    }

    // selected products variable
    let selectedProductsCollection = []

    // initial query
    let query: Query<companyagreementApi.Product> = new Query()

    // where clause to filter products based on the provided query parameters
    const whereTypes: {
      isFavourite?: boolean
      serviceCategory?: 'welfareArt100' | 'welfareArt51' | 'none'
      typology?: 'moneyTransfer' | 'refund' | 'giftCard' | 'goodAndService' | 'pass' | 'voucher' | 'discount'
    } = {}

    // where clause to filter products based on the keywoard search in productName and description
    const whereText: {
      productName?: PredicateFunction<string>
      description?: PredicateFunction<string>
    } = {}

    // if name is present add to where clause and query
    if (key) {
      whereText.productName = (productName: string) => productName?.toLowerCase().includes(key.toLowerCase())
      whereText.description = (description: string) => description?.toLowerCase().includes(key.toLowerCase())

      query = query.and(
        query.or(
          query.where({
            productName: whereText.productName,
          }),
          query.where({
            description: whereText.description,
          }),
        ),
      )
    }

    // if onlyFavourites is true, add to where clause, query and return the filtered products
    if (onlyFavourites === true) {
      whereTypes.isFavourite = true

      query = query.and(query.where(whereTypes))

      selectedProductsCollection = productCollection.findMany(query)
      const response = convertToPaginatedResponse(selectedProductsCollection)
      return HttpResponse.json(response)
    }

    // if kind is provided and is either 'welfareArt100', 'welfareArt51' or 'none', add to where clause
    if (kind && (kind === 'welfareArt100' || kind === 'welfareArt51' || kind === 'none')) {
      whereTypes.serviceCategory = kind
    }

    // if categoryId is main category filter by typology to reduce the initial query result
    if (categoryId === 'DISCOUNT') {
      whereTypes.typology = 'discount'
    } else if (categoryId === 'TRANSFER') {
      whereTypes.typology = 'moneyTransfer'
    } else if (categoryId === 'REFUND') {
      whereTypes.typology = 'refund'
    }

    // update the query to include the where clause for serviceCategory and typology
    query = query.and(query.where(whereTypes))

    // query the collection with the where clause
    selectedProductsCollection = productCollection.findMany(query)

    // if categoryId is provided, filter the products by categoryId. This is done after the initial query to reduce the number of products to filter in case of main categories
    if (categoryId) {
      selectedProductsCollection = selectedProductsCollection?.filter((p) =>
        p.categoryIds?.some((id) => id.startsWith(categoryId)),
      )
    }

    // if no products are found, return an empty response
    if (!selectedProductsCollection.length) {
      return HttpResponse.json(emptyResponse)
    } else {
      const response = convertToPaginatedResponse(selectedProductsCollection)
      return HttpResponse.json(response)
    }
  },
)
